<template>
  <div class="modern-master-view pb-12">
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm">
          <v-icon color="white">fas fa-chart-pie</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Dashboard Finance</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">
            CxC, CxP y tesorería · estado de resultado {{ periodLabel }}
          </div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn outlined color="primary" class="font-weight-black text-uppercase tracking-widest rounded-lg px-6" :loading="loading" @click="fetchAll">
        <v-icon left small>fas fa-redo</v-icon> Actualizar
      </v-btn>
    </v-row>

    <v-card outlined class="modern-card pa-6 mb-6 mx-4">
      <v-row dense align="center">
        <v-col cols="12" md="2">
          <v-text-field v-model.number="year" type="number" outlined dense hide-details label="Año" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="3">
          <v-select v-model="month" :items="monthItems" item-text="text" item-value="value" outlined dense hide-details label="Mes" class="modern-input" @change="fetchAll" />
        </v-col>
        <v-col cols="12" md="7" class="text-right">
          <v-btn text color="primary" class="font-weight-bold" @click="goCurrentMonth">Mes actual</v-btn>
        </v-col>
      </v-row>
    </v-card>

    <v-card v-if="alerts.length" outlined class="modern-card pa-4 mb-6 mx-4">
      <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-3">Alertas</div>
      <v-chip
        v-for="alert in alerts"
        :key="alert.code"
        class="mr-2 mb-2"
        :color="alert.severity === 'error' ? 'error' : 'warning'"
        text-color="white"
        @click="go(alert.href)"
      >
        <strong class="mr-1">{{ alert.title }}</strong>
        {{ alert.message }}
      </v-chip>
    </v-card>

    <v-row class="mb-2 px-4">
      <v-col cols="12">
        <div class="caption font-weight-black text-uppercase tracking-widest grey--text">Estado de resultado · {{ periodLabel }}</div>
      </v-col>
    </v-row>
    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/ar')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Ventas</div>
          <div class="text-h5 font-weight-black primary--text">{{ kpis.sales_month | moneda }}</div>
          <div class="caption grey--text">Cobrado {{ kpis.collections_month | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/ap')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Compras</div>
          <div class="text-h5 font-weight-black">{{ kpis.purchases_month | moneda }}</div>
          <div class="caption grey--text">{{ kpis.purchases_count || 0 }} documentos del período</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="4">
        <v-card outlined class="modern-card pa-6">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Resultado operación</div>
          <div class="text-h5 font-weight-black" :class="(kpis.operating_result || 0) >= 0 ? 'success--text' : 'error--text'">
            {{ kpis.operating_result | moneda }}
          </div>
          <div class="caption grey--text">Ventas − compras · {{ formatPct(kpis.operating_margin_pct) }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/ar')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">CxC abierta</div>
          <div class="text-h5 font-weight-black">{{ kpis.ar_open | moneda }}</div>
          <div class="caption error--text">Vencido {{ kpis.ar_overdue | moneda }} · saldo actual</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/ap')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">CxP abierta</div>
          <div class="text-h5 font-weight-black">{{ kpis.ap_open | moneda }}</div>
          <div class="caption error--text">Vencido {{ kpis.ap_overdue | moneda }} · saldo actual</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/treasury')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Tesorería</div>
          <div class="text-h5 font-weight-black success--text">{{ kpis.treasury_total | moneda }}</div>
          <div class="caption grey--text">Proy. 30d {{ kpis.projected_balance_30 | moneda }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/reconciliation')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Sin conciliar</div>
          <div class="text-h5 font-weight-black">{{ kpis.unmatched_movements || 0 }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="mb-6 px-4">
      <v-col cols="12" sm="6" md="6">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/ot-profitability')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Margen OT del mes</div>
          <div class="text-h5 font-weight-black">{{ kpis.ot_margin_month | moneda }}</div>
          <div class="caption grey--text">{{ kpis.ot_count_month || 0 }} OT · {{ formatPct(kpis.ot_margin_pct_month) }}</div>
        </v-card>
      </v-col>
      <v-col cols="12" sm="6" md="6">
        <v-card outlined class="modern-card pa-6" @click="go('/finance/budgets')">
          <div class="caption font-weight-black text-uppercase tracking-widest grey--text mb-1">Presupuesto del mes</div>
          <div class="text-h5 font-weight-black">{{ kpis.budget_actual | moneda }}</div>
          <div class="caption grey--text">Vs {{ kpis.budget_total | moneda }} · var. {{ kpis.budget_variance | moneda }}</div>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="px-4">
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6">
          <div class="subtitle-2 font-weight-bold mb-3">Antigüedad CxC <span class="caption grey--text font-weight-regular">saldo actual</span></div>
          <v-row dense>
            <v-col v-for="bucket in arAging" :key="'ar-' + bucket.key" cols="6" md="4">
              <div class="caption grey--text">{{ bucket.label }}</div>
              <div class="font-weight-black">{{ bucket.balance | moneda }}</div>
              <div class="caption">{{ bucket.count }} docs</div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card outlined class="modern-card pa-6">
          <div class="subtitle-2 font-weight-bold mb-3">Antigüedad CxP <span class="caption grey--text font-weight-regular">saldo actual</span></div>
          <v-row dense>
            <v-col v-for="bucket in apAging" :key="'ap-' + bucket.key" cols="6" md="4">
              <div class="caption grey--text">{{ bucket.label }}</div>
              <div class="font-weight-black">{{ bucket.balance | moneda }}</div>
              <div class="caption">{{ bucket.count }} docs</div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import notifications from '@/utils/notifications'
import { fetchFinanceDashboard } from '@/api/finance'

const MONTHS = [
  { value: 1, text: 'Enero' }, { value: 2, text: 'Febrero' }, { value: 3, text: 'Marzo' },
  { value: 4, text: 'Abril' }, { value: 5, text: 'Mayo' }, { value: 6, text: 'Junio' },
  { value: 7, text: 'Julio' }, { value: 8, text: 'Agosto' }, { value: 9, text: 'Septiembre' },
  { value: 10, text: 'Octubre' }, { value: 11, text: 'Noviembre' }, { value: 12, text: 'Diciembre' },
]

export default {
  name: 'FinanceDashboard',
  data() {
    const now = new Date()
    return {
      loading: false,
      year: now.getFullYear(),
      month: now.getMonth() + 1,
      kpis: {},
      alerts: [],
      arAging: [],
      apAging: [],
      monthItems: MONTHS,
    }
  },
  computed: {
    periodLabel() {
      const found = this.monthItems.find(item => item.value === Number(this.month))
      return found ? `${found.text} ${this.year}` : String(this.year)
    },
  },
  methods: {
    formatPct(value) {
      if (value === null || value === undefined) return '—'
      return `${Number(value).toFixed(1)}%`
    },
    go(href) {
      if (href) this.$router.push(href)
    },
    goCurrentMonth() {
      const now = new Date()
      this.year = now.getFullYear()
      this.month = now.getMonth() + 1
      this.fetchAll()
    },
    async fetchAll() {
      this.loading = true
      try {
        const data = await fetchFinanceDashboard({ year: this.year, month: this.month })
        this.kpis = data.kpis || {}
        this.alerts = data.alerts || []
        this.arAging = data.ar_aging || []
        this.apAging = data.ap_aging || []
      } catch (e) {
        notifications.showError('No se pudo cargar el dashboard de Finance')
      } finally {
        this.loading = false
      }
    },
  },
  created() {
    this.fetchAll()
  },
}
</script>
